import { useState, useEffect } from 'react';
import { getUnreadCount } from '../services/messageService';

/**
 * Shared unread-message poller.
 *
 * Several persistent layout components (Navigation, WebTopBar, UserBar) all show
 * the unread count. Previously each one ran its own 30s setInterval, so a single
 * session called the getUnreadConversationCount cloud function 2-3x every 30s.
 * This module keeps ONE poller and fans the result out to every subscriber, so
 * the function is called at most once per interval no matter how many components
 * are mounted. Polling also skips while the browser tab is hidden, and refreshes
 * immediately when it becomes visible again.
 */

const POLL_INTERVAL_MS = 30000;

let sharedCount = 0;
let currentUserId: string | undefined;
let intervalId: ReturnType<typeof setInterval> | null = null;
let inFlight = false;
const subscribers = new Set<(n: number) => void>();

function notify() {
  for (const cb of subscribers) cb(sharedCount);
}

async function fetchUnread() {
  if (inFlight) return; // don't overlap a slow request with the next tick
  if (typeof document !== 'undefined' && document.hidden) return; // skip while tab hidden
  inFlight = true;
  try {
    const count = await getUnreadCount();
    if (count !== sharedCount) {
      sharedCount = count;
      notify();
    }
  } catch {
    // silently ignore — keep last known count
  } finally {
    inFlight = false;
  }
}

function startPolling(userId: string) {
  currentUserId = userId;
  fetchUnread();
  if (intervalId == null) {
    intervalId = setInterval(fetchUnread, POLL_INTERVAL_MS);
  }
}

function stopPolling() {
  if (intervalId != null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  currentUserId = undefined;
  sharedCount = 0;
}

// Refresh as soon as the tab regains focus, so the count isn't stale for up to a
// full interval after the user comes back.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && currentUserId && subscribers.size > 0) {
      fetchUnread();
    }
  });
}

export function useUnreadMessages(userId: string | undefined): number {
  const [unreadMessages, setUnreadMessages] = useState(sharedCount);

  useEffect(() => {
    if (!userId) {
      setUnreadMessages(0);
      return;
    }

    // Signed-in user changed — drop the previous user's count.
    if (currentUserId && currentUserId !== userId) {
      sharedCount = 0;
    }

    subscribers.add(setUnreadMessages);
    setUnreadMessages(sharedCount);

    // Only (re)start the shared poller if it isn't already running for this user.
    if (currentUserId !== userId || intervalId == null) {
      startPolling(userId);
    }

    return () => {
      subscribers.delete(setUnreadMessages);
      if (subscribers.size === 0) {
        stopPolling();
      }
    };
  }, [userId]);

  return unreadMessages;
}
